import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import get from 'lodash/get';
import Skeleton from 'react-loading-skeleton';
import Button from '@material-ui/core/Button';
import PulseLoader from 'react-spinners/PulseLoader';

// app modules: constants
import { COLOURS } from 'constants/index';

// app: components
import Comment from 'components/Comments/Comment';

// app: props
import CommentProps from 'components/Comments/Comment/CommentProps';

// app: queries
import { GET_ALL_RECIPE_COMMENTS, GET_COMMENT_OFFSET } from '../CommentQueries';

const PAGE_SIZE = 10;

function CommentStream(props) {
  const {
    contentId,
    type,
    focusedCommentId,
    focusedCommentResponding,
    fallbackComment,
    showPlaceholders,
    readyToScroll,
    onEditQuestionClick,
    onLoad,
    hasPrimaryImage,
    isModerator,
  } = props;
  const offsetRef = useRef(0);
  const deepLinkTargetRef = useRef(null);
  const [scrollWasTriggered, setScrollWasTriggered] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const userId = useSelector(state => state.user.id);

  // When the tab changes, reset the pagination offset
  useEffect(
    () => {
      offsetRef.current = 0;
    },
    [type],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (deepLinkTargetRef.current && !scrollWasTriggered && readyToScroll) {
      deepLinkTargetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setScrollWasTriggered(true);
    }
  });

  // If there is a URL hash specifying a commentId then we need to lookup the offset of that comment so we can page straight to it
  const { data: deepLinkData } = useQuery(GET_COMMENT_OFFSET, {
    variables: { comment_id: parseInt(focusedCommentId, 10) },
    skip: !focusedCommentId,
    fetchPolicy: 'cache-and-network',
    ssr: false,
  });

  // Load all comments for this recipe (cacheable)
  // We will start on the page of the comment specified in the url hash if present.
  const deepLinkedCommentOffset = get(deepLinkData, 'RecipeComment.location.offset');
  const latestCommentId = get(deepLinkData, 'RecipeComment.location.latest_id');

  const limit = Math.ceil(deepLinkedCommentOffset / PAGE_SIZE) * PAGE_SIZE || PAGE_SIZE;
  const offset = 0;
  const { loading, error, data, fetchMore } = useQuery(GET_ALL_RECIPE_COMMENTS, {
    variables: {
      content_id: contentId,
      type,
      offset,
      limit,
      include_rejected: isModerator,
      response_limit: 50, // TODO pagination and set this to a lower value
      latest_id: latestCommentId, // Cache buster
      _authenticated: isModerator,
    },
    ssr: false,
    returnPartialData: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: onLoad,
    onError: err => {
      console.error('Failed to load all comments', err);
    },
  });
  const allComments = get(data, 'RecipeComments') || [];

  const handleLoadMoreComments = () => {
    if (!offsetRef.current) {
      offsetRef.current = limit;
    } else {
      offsetRef.current += PAGE_SIZE;
    }
    fetchMore({
      notifyOnNetworkStatusChange: true,
      variables: {
        content_id: contentId,
        type,
        offset: offsetRef.current,
        limit: PAGE_SIZE,
        latest_id: latestCommentId, // Cache buster
      },
    });
  };

  const handleCommentClick = (event, comment) => {
    if (event.target.ariaLabel === 'edit-question') {
      onEditQuestionClick(comment);
    }

    setSelectedCommentId(comment.id);
  };

  return (
    <React.Fragment>
      {allComments
        ? allComments.map(comment => (
            <Comment
              key={`comment-${comment.id}`}
              comment={comment}
              hasPrimaryImage={hasPrimaryImage}
              isModerator={isModerator}
              focused={comment.id === focusedCommentId && allComments.length > 1}
              responding={comment.id === focusedCommentId && focusedCommentResponding}
              ref={comment.id === focusedCommentId ? deepLinkTargetRef : null}
              withModeratorControls={selectedCommentId === comment.id && isModerator}
              selected={selectedCommentId === comment.id}
              onClick={handleCommentClick}
              userIsCommenter={comment.user.id === userId}
              userHasResponded={comment.responses?.some(response => response.user.id === userId)}
            >
              {comment.responses
                ? comment.responses.map(response => (
                    <Comment
                      level={1}
                      hasPrimaryImage={hasPrimaryImage}
                      key={`response-${response.id}`}
                      comment={response}
                      withModeratorControls={selectedCommentId === response.id && isModerator}
                      selected={selectedCommentId === response.id}
                      onClick={handleCommentClick}
                      userIsCommenter={response.user.id === userId}
                      parentComment={comment}
                      isModerator={isModerator}
                    />
                  ))
                : null}
            </Comment>
          ))
        : null}
      {allComments.length >= PAGE_SIZE && allComments.length % limit === 0 ? (
        <Button onClick={handleLoadMoreComments} disabled={loading} color="primary">
          {loading ? (
            <PulseLoader loading={loading} color={COLOURS.ENERGIZED} sizeUnit="px" size={8} />
          ) : (
            'Load more'
          )}
        </Button>
      ) : null}
      {!loading && !allComments.length && !fallbackComment && showPlaceholders ? (
        <div className="comments__empty padding-top-10 padding-bottom-40">
          <p>No {type}s for this recipe</p>
        </div>
      ) : null}
      {loading && !allComments.length && showPlaceholders ? (
        <div>
          <Skeleton height="68px" />
        </div>
      ) : null}
      {!allComments.length && !loading && fallbackComment ? (
        /* Might be used while waiting for the cached response to include our comment */
        <Comment
          comment={fallbackComment}
          hasPrimaryImage={hasPrimaryImage}
          onClick={handleCommentClick}
          selected={selectedCommentId === fallbackComment.id}
          focused={fallbackComment.id === focusedCommentId}
          withModeratorControls={selectedCommentId === fallbackComment.id && isModerator}
          ref={fallbackComment.id === focusedCommentId ? deepLinkTargetRef : null}
          isModerator={isModerator}
        />
      ) : null}
      {error ? <p>Something went wrong loading the {type}s</p> : null}
    </React.Fragment>
  );
}

CommentStream.propTypes = {
  contentId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  focusedCommentId: PropTypes.number,
  focusedCommentResponding: PropTypes.bool,
  hasPrimaryImage: PropTypes.bool,
  fallbackComment: CommentProps,
  onLoad: PropTypes.func,
  onEditQuestionClick: PropTypes.func,
  readyToScroll: PropTypes.bool,
  showPlaceholders: PropTypes.bool,
  isModerator: PropTypes.bool,
};
CommentStream.defaultProps = {
  focusedCommentId: null,
  focusedCommentResponding: false,
  hasPrimaryImage: false,
  fallbackComment: null,
  onLoad: () => {},
  onEditQuestionClick: () => {},
  readyToScroll: false,
  showPlaceholders: true,
  isModerator: false,
};

export default React.memo(CommentStream);
